import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Trash2Icon,
  PencilIcon,
  MoveUp,
  MoveDown,
  ArrowLeftRight,
  File,
} from "lucide-react";
import { useAuth } from "@/context/AuthProvider";
import { supabase } from "@/lib/supabase";
import { useAccountDetail } from "@/hooks/useAccountDetail";
import {
  createTransfer,
  updateTransfer,
  deleteTransactionOrTransfer,
} from "@/lib/transfers";
import { TransactionFormDialog } from "@/components/TransactionFormDialog";
import { DeleteDialog } from "@/components/DeleteDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useCurrentSubscription } from "@/hooks/useCurrentSubscription";
import { FilePreview } from "@/components/file-preview";
import { Badge } from "@/components/ui/badge";

export default function AccountDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  });
  const { subscription, status, daysLeft, loading } = useCurrentSubscription();

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    txId: null,
  });
  const {
    account,
    transactions,
    accountsMap,
    ingresos,
    egresos,
    total,
    fetchData,
    balanceTotal,
  } = useAccountDetail(
    id,
    dateRange.from.toISOString().split("T")[0],
    dateRange.to.toISOString().split("T")[0]
  );

  useEffect(() => {
    if (id) fetchData();
  }, [id, dateRange]);

  const filtered =
    transactions &&
    transactions.filter((t: any) =>
      t?.description === null
        ? "---"
        : t?.description?.toLowerCase().includes(search.toLowerCase())
    );

  async function onSubmit(values: any) {
    const isEditing = !!editTx;
    const isTransfer = !!values.to_account_id;

    const txPayload: any = {
      amount: values.amount,
      date: values.date,
      description: values.description,
      type: values.type,
    };

    if (values.attachment_url) {
      txPayload.attachment_url = values.attachment_url;
    }

    if (isTransfer) {
      if (isEditing && editTx.transfer_id) {
        const success = await updateTransfer({
          transfer_id: editTx.transfer_id,
          ...txPayload,
        });

        if (!success) return;
      } else {
        const transfer = await createTransfer({
          from_account_id: id,
          to_account_id: values.to_account_id,
          user_id: user?.id,
          ...txPayload,
        });

        if (!transfer) return;
      }
    } else {
      txPayload.category_id = values.category_id;
      if (isEditing) {
        await supabase
          .from("transactions")
          .update(txPayload)
          .eq("id", editTx?.id);
      } else {
        await supabase.from("transactions").insert({
          ...txPayload,
          account_id: id,
          user_id: user?.id,
        });
      }
    }

    setOpen(false);
    setEditTx(null);
    fetchData();
  }

  async function onDelete(tx) {
    setConfirmDialog({ open: true, tx });
  }

  function onEdit(tx: any) {
    tx.to_account_id = tx?.transfers?.to_account_id;
    setEditTx(tx);
    setOpen(true);
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex md:flex-row md:justify-between md:items-center flex-col">
        <h2 className="text-2xl font-bold">{account?.name}</h2>
        <Popover>
          <PopoverTrigger asChild>
            <button className="p-2 px-5 rounded-lg border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50">
              {format(dateRange.from, "dd MMM yyyy")} -{" "}
              {format(dateRange.to, "dd MMM yyyy")}
            </button>
          </PopoverTrigger>
          <PopoverContent>
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={(range) => {
                if (range?.from && range?.to) {
                  setDateRange({ from: range.from, to: range.to });
                }
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex overflow-auto  gap-4 overscroll-auto flex-nowrap">
        <Card style={{ minWidth: 200 }}>
          <CardContent className="p-4">
            <p className="text-sm">Balance Total</p>
            <p className="text-xl font-semibold">
              {account?.currency} {balanceTotal?.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card style={{ minWidth: 200 }}>
          <CardContent className="p-4">
            <p className="text-sm">Balance del periodo</p>
            <p className="text-xl font-semibold">
              {account?.currency} {total?.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card style={{ minWidth: 200 }}>
          <CardContent className="p-4">
            <p className="text-sm">Ingresos del periodo</p>
            <p className="text-xl font-semibold text-green-600">
              {account?.currency} {ingresos?.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card style={{ minWidth: 200 }}>
          <CardContent className="p-4">
            <p className="text-sm">Egresos del periodo</p>
            <p className="text-xl font-semibold text-red-600">
              {account?.currency} {egresos?.toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between items-center">
        <Input
          placeholder="Buscar transacción..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <TransactionFormDialog
          open={open}
          setOpen={setOpen}
          onSubmit={onSubmit}
          editTx={editTx}
          setEditTx={setEditTx}
          accountsMap={accountsMap}
          accountId={id}
        />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead>Descripción</TableHead>
            <TableHead className="text-right">Monto</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Categoria</TableHead>
            <TableHead className="text-right">Adjunto</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered &&
            filtered.map((t: any) => (
              <TableRow key={t.id}>
                <TableCell>{t.date}</TableCell>

                <TableCell>
                  {t.description
                    ? t.transfer_id
                      ? t.description +
                        (t?.transfers?.from_account.id === id
                          ? "(hacia " + t?.transfers?.to_account.name + ")"
                          : "(desde " + t?.transfers?.from_account.name + ")")
                      : t.description
                    : "--"}
                </TableCell>
                <TableCell className="text-right">
                  {account?.currency} {t.amount.toFixed(2)}
                </TableCell>
                <TableCell>
                  {/* {t.transfer_id ? "transfer" : t.type} */}
                  {t.transfer_id ? (
                    <ArrowLeftRight size={18} />
                  ) : (
                    <>
                      {t.type === "income" && (
                        <MoveUp size={18} color="green" />
                      )}
                      {t.type === "expense" && (
                        <MoveDown size={18} color="red" />
                      )}
                    </>
                  )}
                </TableCell>

                <TableCell className="text-right">
                  {t?.categories?.name && (
                    <Badge variant="default">{t?.categories?.name}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <FilePreview url={t?.attachment_url}> </FilePreview>
                </TableCell>
                <TableCell className="text-right space-x-2">
                  {t.transfer_id ? (
                    <>
                      {t?.transfers?.from_account_id === id && (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onEdit(t)}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => onDelete(t)}
                          >
                            <Trash2Icon className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {status === "active" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => onEdit(t)}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => onDelete(t)}
                      >
                        <Trash2Icon className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <DeleteDialog
        open={confirmDialog.open}
        setOpen={(open) => !open && setConfirmDialog({ open: false, tx: null })}
        onConfirm={() => {
          deleteTransactionOrTransfer(confirmDialog.tx);
          setConfirmDialog({ open: false, txId: null });
          fetchData();
        }}
        title="¿Eliminar transacción?"
        description="Esta acción eliminará la transacción o transferencia vinculada."
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
}
